import React from 'react';
import { Card, Col, Placeholder, Row } from 'react-bootstrap';
import CardLayout from '../../../../../../shared/components/card/CardLayout';

const SectionHeader = ({ withCog = true }: { withCog?: boolean }) => (
    <div className="d-flex justify-content-between align-items-center mb-3">
        <Placeholder className="skeleton skeleton-section-title" />
        {withCog && <Placeholder className="skeleton skeleton-header-icon" />}
    </div>
);

const Label = ({ className = 'mb-2' }: { className?: string }) => (
    <Placeholder className={`skeleton skeleton-stat-label d-block ${className}`} />
);

const LabelValue = ({ className = '' }: { className?: string }) => (
    <div className={className}>
        <Label />
        <Placeholder className="skeleton skeleton-table-name d-block" />
    </div>
);

const TextLines = ({ widths }: { widths: number[] }) => (
    <>
        {widths.map((width, i) => (
            <Placeholder key={i} xs={width} className="skeleton skeleton-description-line d-block mb-2" />
        ))}
    </>
);

const ChipsSkeleton = ({ count = 3 }: { count?: number }) => (
    <div className="d-flex flex-wrap">
        {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="my-2 mr-2">
                <div className="card py-2 px-4 campaign-multiselect contract-1 d-flex flex-row align-items-center">
                    <Placeholder className="skeleton skeleton-chip-icon" />
                    <Placeholder className="skeleton skeleton-table-cell ml-2" />
                </div>
            </div>
        ))}
    </div>
);

const FileBoxesSkeleton = ({ height, count = 3 }: { height: number; count?: number }) => (
    <Row>
        {Array.from({ length: count }).map((_, i) => (
            <Col key={i} md={4} className="my-2">
                <Placeholder className="skeleton skeleton-file-box" style={{ height }} />
                <Placeholder xs={6} className="skeleton skeleton-description-line d-block mx-auto mt-2" />
            </Col>
        ))}
    </Row>
);

const ImageTextCardSkeleton = () => (
    <Card className="mb-4">
        <div className="d-sm-flex">
            <Col md={4} className="p-3 p-sm-0">
                <Placeholder className="skeleton skeleton-distribution-image" />
            </Col>
            <Col md={8} className="d-flex align-items-center">
                <div className="p-3 w-100">
                    <Placeholder xs={6} className="skeleton skeleton-title d-block mb-3" />
                    <TextLines widths={[12, 9]} />
                </div>
            </Col>
        </div>
    </Card>
);

export function BriefingOverviewLeftSkeleton({ withBudgetCard = true }: { withBudgetCard?: boolean }) {
    return (
        <Placeholder as="div" animation="glow" aria-hidden="true">
            <CardLayout classCard="mb-3">
                <SectionHeader withCog={false} />
                <Row>
                    <Col md={4} className="d-flex flex-column align-items-center justify-content-center">
                        <Placeholder className="skeleton skeleton-section-icon mb-2" />
                        <Placeholder className="skeleton skeleton-table-cell skeleton-table-cell-wide" />
                    </Col>
                    <Col md={8}>
                        <div className="py-2">
                            <Placeholder xs={6} className="skeleton skeleton-title d-block mb-3" />
                            <TextLines widths={[12, 9]} />
                        </div>
                    </Col>
                </Row>
            </CardLayout>

            <CardLayout classCard="mb-3">
                <SectionHeader />
                <Label className="mb-3" />
                <Row className="mb-4">
                    <Col sm={4}>
                        <Placeholder className="skeleton skeleton-goal-image" />
                    </Col>
                    <Col sm={8} className="mt-3 mt-sm-0">
                        <Placeholder xs={5} className="skeleton skeleton-title d-block mb-3" />
                        <TextLines widths={[12, 12, 10, 7]} />
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <Label />
                        <Card className="create-campaign-card mb-2 mb-md-0">
                            <Placeholder className="skeleton skeleton-creator-image" />
                            <div className="p-3">
                                <div className="d-flex justify-content-between mb-2">
                                    <Placeholder xs={5} className="skeleton" />
                                    <Placeholder xs={4} className="skeleton" />
                                </div>
                                <TextLines widths={[12, 8]} />
                            </div>
                        </Card>
                    </Col>
                    <Col md={8}>
                        <Label className="mt-3 mt-md-0" />
                        <ChipsSkeleton count={3} />
                        <div className="d-flex mt-4" style={{ gap: 48 }}>
                            <LabelValue />
                            <LabelValue />
                        </div>
                    </Col>
                </Row>
            </CardLayout>

            <CardLayout classCard="mb-3">
                <SectionHeader />
                {Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className="mb-4">
                        <Label />
                        <TextLines widths={[12, 11, 6]} />
                    </div>
                ))}
                <LabelValue className="mb-4" />
                <LabelValue className="mb-4" />
                <Label />
                <FileBoxesSkeleton height={130} />
            </CardLayout>

            <CardLayout classCard="mb-3">
                <SectionHeader />
                <div className="d-flex flex-wrap justify-content-between" style={{ columnGap: 44, rowGap: 28 }}>
                    {Array.from({ length: 4 }).map((_, i) => (
                        <LabelValue key={i} />
                    ))}
                </div>
            </CardLayout>

            <CardLayout classCard="mb-3">
                <SectionHeader />
                <div className="d-flex flex-wrap" style={{ gap: 16 }}>
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="d-flex align-items-center my-1 mr-3">
                            <Placeholder className="skeleton skeleton-icon" />
                            <Placeholder className="skeleton skeleton-table-cell ml-2" />
                            <Placeholder className="skeleton skeleton-table-cell skeleton-table-cell-sm ml-2" />
                        </div>
                    ))}
                </div>
            </CardLayout>

            <CardLayout classCard="mb-3">
                <SectionHeader />
                <div className="d-flex flex-wrap mb-3" style={{ gap: 20 }}>
                    {Array.from({ length: 3 }).map((_, i) => (
                        <LabelValue key={i} className="mr-5" />
                    ))}
                </div>
                <Label />
                <ChipsSkeleton count={4} />
            </CardLayout>

            <CardLayout classCard="mb-3">
                <SectionHeader />
                <div className="d-flex flex-wrap mb-3" style={{ gap: 18 }}>
                    {Array.from({ length: 3 }).map((_, i) => (
                        <LabelValue key={i} className="mr-5" />
                    ))}
                </div>
                <Label />
                <TextLines widths={[12, 8]} />
            </CardLayout>

            <CardLayout classCard="mb-3">
                <SectionHeader />
                <ImageTextCardSkeleton />
                <Label />
                <ImageTextCardSkeleton />
                <Label className="mt-3 mb-2" />
                <ImageTextCardSkeleton />
            </CardLayout>

            <CardLayout classCard="mb-3">
                <SectionHeader />
                <Row style={{ rowGap: 16 }}>
                    <Col md={6}>
                        <LabelValue />
                    </Col>
                    <Col md={12}>
                        <LabelValue />
                    </Col>
                    <Col md={6}>
                        <LabelValue />
                    </Col>
                    <Col md={6}>
                        <LabelValue />
                    </Col>
                    <Col md={12}>
                        <Label />
                        <TextLines widths={[12, 9]} />
                        <Label className="mt-3 mb-2" />
                        <FileBoxesSkeleton height={140} />
                    </Col>
                </Row>
            </CardLayout>

            {withBudgetCard && (
                <CardLayout classCard="mb-3">
                    <Row className="align-items-start" style={{ rowGap: 16 }}>
                        <Col md={3}>
                            <Label className="mb-md-3" />
                            <Placeholder className="skeleton skeleton-stat-value-lg d-block" />
                        </Col>
                        <Col md={3}>
                            <Label className="mb-md-3" />
                            <Placeholder className="skeleton skeleton-input d-block" />
                        </Col>
                        <Col md={6} className="d-flex flex-column align-items-md-end">
                            <Label className="mb-md-3" />
                            <Placeholder className="skeleton skeleton-stat-value-xl d-block" />
                        </Col>
                    </Row>
                    <div className="d-flex align-items-center mt-4" style={{ gap: 16 }}>
                        <Placeholder className="skeleton skeleton-table-cell" />
                        <Placeholder className="skeleton skeleton-toggle" />
                        <Placeholder className="skeleton skeleton-table-cell skeleton-table-cell-wide" />
                        <Placeholder className="skeleton skeleton-toggle" />
                    </div>
                    <div className="d-flex justify-content-between mt-4">
                        <Placeholder className="skeleton skeleton-outline-btn skeleton-btn-sm" />
                        <Placeholder className="skeleton skeleton-action-btn skeleton-btn-sm skeleton-btn-wide" />
                    </div>
                </CardLayout>
            )}
        </Placeholder>
    );
}

export function CampaignOverviewCardSkeleton() {
    return (
        <Card className="mb-4 mt-4 mt-md-0 overflow-hidden" aria-hidden="true">
            <Card.Body>
                <Placeholder as="div" animation="glow">
                    <Row>
                        <Col md={6}>
                            <Placeholder className="skeleton skeleton-overview-image" />
                        </Col>
                        <Col md={6} className="d-flex flex-column justify-content-between mt-3 mt-md-0">
                            <div className="d-flex justify-content-between align-items-center">
                                <Placeholder xs={8} className="skeleton skeleton-title" />
                                <Placeholder className="skeleton skeleton-header-icon" />
                            </div>
                            <Placeholder className="skeleton skeleton-badge mt-3 d-none d-md-block" />
                            {Array.from({ length: 2 }).map((_, i) => (
                                <div key={i} className="mt-3">
                                    <Placeholder className="skeleton skeleton-table-name d-block mb-1" />
                                    <Placeholder className="skeleton skeleton-stat-label d-block" />
                                </div>
                            ))}
                        </Col>
                    </Row>
                    <Row className="mt-4" style={{ rowGap: 10 }}>
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Col key={i} xs={6} md={4} className="d-flex flex-column align-items-center my-2">
                                <Placeholder className="skeleton skeleton-table-cell skeleton-table-cell-wide" />
                                <Placeholder className="skeleton skeleton-stat-label mt-2" />
                            </Col>
                        ))}
                    </Row>
                    <Placeholder className="skeleton skeleton-outline-btn skeleton-btn-block d-block mt-3" />
                </Placeholder>
            </Card.Body>
        </Card>
    );
}
function LinkedCampaignsCardSkeleton() {
    return (
        <div className="my-4">
            <CardLayout classBody="new-cards px-3 py-0">
                <div className="card-row">
                    <div className="image-wrapper">
                        <Placeholder className="skeleton skeleton-fill" />
                    </div>
                    <div className="card-content">
                        <Placeholder className="skeleton skeleton-table-name d-block" />
                        <Placeholder className="skeleton skeleton-date d-block mt-2" />
                    </div>
                    <div className="button-wrapper">
                        <Placeholder className="skeleton skeleton-outline-btn skeleton-outline-btn-lg" />
                    </div>
                </div>
            </CardLayout>
        </div>
    );
}

export function LinkedCampaignsSectionSkeleton({ count = 2 }: { count?: number }) {
    return (
        <Placeholder as="div" animation="glow" className="mb-4 mt-4 mt-md-0" aria-hidden="true">
            <Placeholder className="skeleton skeleton-table-name d-block" />
            <hr className="default-border-lighter" />
            {Array.from({ length: count }).map((_, i) => (
                <LinkedCampaignsCardSkeleton key={i} />
            ))}
        </Placeholder>
    );
}
export function LinkToPublicCardSkeleton() {
    return (
        <CardLayout>
            <Placeholder as="div" animation="glow" aria-hidden="true">
                <Placeholder className="skeleton skeleton-section-title d-block mb-2" />
                <div className="d-flex align-items-center">
                    <div className="col mr-3 px-0">
                        <Placeholder className="skeleton skeleton-input d-block" />
                    </div>
                    <Placeholder className="skeleton skeleton-action-btn" style={{ width: 70, height: 38 }} />
                </div>
            </Placeholder>
        </CardLayout>
    );
}
